import { CircularProgress, styled } from '@mui/material'
import { VersionReportTemplateEntity } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import moment from 'moment'
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded'
import {
  LoadingContainer,
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  HeaderCell,
  DataCell,
  NameCell,
  NameText,
  FolderRowIcon,
  DialogContent,
  SelectMenuHeader,
  BreadcrumbListIcon,
  BreadcrumbCurrent,
  AddButton,
} from './commons'
import { StyledDivider } from '../commons'
import { RenameMenu } from './RenameMenu'
import { memo, useCallback, useState } from 'react'
import { intl } from '../../../../../i18n'

interface TemplateMenuProps {
  templates: VersionReportTemplateEntity[]
  loading: boolean
  onTemplateSelect: (template: VersionReportTemplateEntity) => void
  onCreateNewTemplate: () => void
  onTemplateUpdate: (template: VersionReportTemplateEntity) => void
  onTemplateDelete: (template: VersionReportTemplateEntity) => void
  onClose: () => void
}

export const TemplateMenu = ({
  templates,
  loading,
  onTemplateSelect,
  onCreateNewTemplate,
  onTemplateUpdate,
  onTemplateDelete,
  onClose,
}: TemplateMenuProps) => {
  const [openMenuUuid, setOpenMenuUuid] = useState<string | null>(null)
  const handleMenuOpenChange = useCallback((uuid: string, isOpen: boolean) => {
    setOpenMenuUuid(isOpen ? uuid : null)
  }, [])

  return (
    <>
      <SelectMenuHeader
        breadcrumb={
          <>
            <BreadcrumbListIcon />
            <BreadcrumbCurrent>フォルダ一覧</BreadcrumbCurrent>
          </>
        }
        actions={
          <AddButton
            onClick={onCreateNewTemplate}
            tooltip="新しくフォルダを作成"
            icon={<CreateNewFolderRoundedIcon sx={{ fontSize: 18 }} />}
          />
        }
        onClose={onClose}
      />
      <StyledDivider />
      <DialogContent>
        {loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <TableContainer>
            <TableHeader>
              <HeaderCell width="52%">名称</HeaderCell>
              <HeaderCell width="22%">更新者</HeaderCell>
              <HeaderCell width="20%">更新日時</HeaderCell>
              <HeaderCell width="6%"></HeaderCell>
            </TableHeader>
            <TableBody>
              {templates.map(template => (
                <TemplateRow
                  key={template.uuid}
                  template={template}
                  isMenuOpen={openMenuUuid === template.uuid}
                  onMenuOpenChange={handleMenuOpenChange}
                  onTemplateSelect={onTemplateSelect}
                  onTemplateUpdate={onTemplateUpdate}
                  onTemplateDelete={onTemplateDelete}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </DialogContent>
    </>
  )
}

const TemplateRow = memo(
  ({
    template,
    isMenuOpen,
    onMenuOpenChange,
    onTemplateSelect,
    onTemplateUpdate,
    onTemplateDelete,
  }: {
    template: VersionReportTemplateEntity
    isMenuOpen: boolean
    onMenuOpenChange: (uuid: string, isOpen: boolean) => void
    onTemplateSelect: (template: VersionReportTemplateEntity) => void
    onTemplateUpdate: (template: VersionReportTemplateEntity) => void
    onTemplateDelete: (template: VersionReportTemplateEntity) => void
  }) => {
    const handleSelect = useCallback(() => {
      onTemplateSelect(template)
    }, [template, onTemplateSelect])

    const handleUpdate = useCallback(() => {
      onTemplateUpdate(template)
    }, [template, onTemplateUpdate])

    const handleDelete = useCallback(() => {
      onTemplateDelete(template)
    }, [template, onTemplateDelete])

    const handleMenuOpenChange = useCallback(
      (isOpen: boolean) => {
        onMenuOpenChange(template.uuid, isOpen)
      },
      [template.uuid, onMenuOpenChange]
    )

    const deleteDisabled = template.versionCount > 0

    return (
      <TemplateTableRow>
        <NameCell width="52%" onClick={handleSelect}>
          <FolderRowIcon />
          <NameText>{template.name}</NameText>
        </NameCell>
        <DataCell width="22%" onClick={handleSelect}>
          {template.updatedBy}
        </DataCell>
        <DataCell width="20%" onClick={handleSelect}>
          {moment(template.updatedAt).format('MM/DD HH:mm')}
        </DataCell>
        <ActionCell width="6%">
          <MenuButtonWrapper className="template-action-menu">
            <RenameMenu
              isOpen={isMenuOpen}
              onOpenChange={handleMenuOpenChange}
              onRename={handleUpdate}
              label={intl.formatMessage({
                id: 'versionReport.template.rename.menuLabel',
              })}
              onDelete={handleDelete}
              deleteLabel={intl.formatMessage({
                id: 'versionReport.template.delete.menuLabel',
              })}
              deleteDisabled={deleteDisabled}
              deleteDisabledReason={intl.formatMessage({
                id: 'versionReport.template.delete.disabledReason',
              })}
            />
          </MenuButtonWrapper>
        </ActionCell>
      </TemplateTableRow>
    )
  }
)

TemplateRow.displayName = 'TemplateRow'

const TemplateTableRow = styled(TableRow)({
  '&:hover .template-action-menu': {
    opacity: 1,
  },
})

const ActionCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}))

const MenuButtonWrapper = styled('div')({
  opacity: 0,
  transition: 'opacity 0.2s',
})
